interface DatadogRumUser {
  id?: string;
  role?: string;
  organizations?: Array<{ id: string; name: string }>;
  cameFromNativeApp?: boolean;
}

interface DatadogRumGlobal {
  onReady: (callback: () => void) => void;
  setUser: (user: DatadogRumUser) => void;
  setUserProperty: (key: string, value: any) => void;
  removeUserProperty: (key: string) => void;
  clearUser: () => void;
  getUser: () => DatadogRumUser | undefined;
  startSessionReplayRecording: () => void;
  addAction: (name: string, context?: any) => void;
  stopSession: () => void;
}

type GraphQLUserData = {
  id?: string;
  role?: string;
  organizations?: Array<{ id: string; name: string; [key: string]: any }>;
  [key: string]: any;
};

declare global {
  interface Window {
    DD_RUM?: DatadogRumGlobal;
    userRole?: string;
    cameFromNativeApp?: boolean;
  }
}

export const isDatadogRumAvailable = (): boolean => {
  return typeof window !== 'undefined' && window.DD_RUM !== undefined;
};

const withDatadogRum = (callback: (rum: DatadogRumGlobal) => void): void => {
  if (!isDatadogRumAvailable()) {
    return;
  }
  const rum = window.DD_RUM;
  if (!rum) return;
  
  rum.onReady(() => callback(rum));
};

const mapOrganizations = (
  organizations?: Array<{ id: string; name: string; [key: string]: any }>
): Array<{ id: string; name: string }> | undefined => {
  return organizations?.map(org => ({
    id: org.id,
    name: org.name,
  }));
};

const getAppSourceContext = (): Partial<DatadogRumUser> => {
  return window.cameFromNativeApp !== undefined 
    ? { cameFromNativeApp: window.cameFromNativeApp }
    : {};
};

const buildDatadogUser = (userData: Partial<DatadogRumUser>): DatadogRumUser => {
  return {
    ...(userData.id && { id: userData.id }),
    ...(userData.role && { role: userData.role }),
    ...(userData.organizations?.length && {
      organizations: mapOrganizations(userData.organizations),
    }),
    ...(userData.cameFromNativeApp !== undefined && { cameFromNativeApp: userData.cameFromNativeApp }),
  };
};

export const setDatadogUser = (user: DatadogRumUser): void => {
  withDatadogRum(rum => {
    const userInfo = buildDatadogUser(user);
    rum.setUser(userInfo);
  });
};

export const updateDatadogUserProperty = (key: string, value: any): void => {
  withDatadogRum(rum => rum.setUserProperty(key, value));
};

export const clearDatadogUser = (): void => {
  withDatadogRum(rum => rum.clearUser());
};

export const getDatadogUser = (): DatadogRumUser | undefined => {
  if (!isDatadogRumAvailable()) {
    return undefined;
  }

  return window.DD_RUM?.getUser();
};

export const initializeDatadogUserFromWindow = (): void => {
  if (!isDatadogRumAvailable()) {
    return;
  }

  const isUserAuthenticated = window.auth?.email;
  if (!isUserAuthenticated) {
    return;
  }

  const contextualUserInfo = { 
    ...(window.userRole && { role: window.userRole }),
    ...getAppSourceContext(),
  };
  
  const hasUserContext = Object.keys(contextualUserInfo).length > 0;
  if (hasUserContext) {
    setDatadogUser(contextualUserInfo);
  }
};

export const updateDatadogUserFromGraphQL = (currentUser: GraphQLUserData | null): void => {
  const hasValidUserId = currentUser?.id && 
                         typeof currentUser.id === 'string' && 
                         currentUser.id.trim().length > 0;
  
  if (!hasValidUserId) {
    return;
  }

  const userInfo = buildDatadogUser({
    ...currentUser,
    ...getAppSourceContext(),
  });

  setDatadogUser(userInfo);
};

export const handleDatadogUserLogout = (): void => {
  clearDatadogUser();
};

export const trackUserLogin = (userId?: string, role?: string): void => {
  withDatadogRum(rum => {
    rum.addAction('user_login', {
      userId,
      role,
    });
  });
};

export const trackUserLogout = (userId?: string): void => {
  withDatadogRum(rum => {
    rum.addAction('user_logout', {
      userId,
    });

    clearDatadogUser();
  });

  if (userId && typeof window !== 'undefined' && window.sessionStorage) {
    sessionStorage.removeItem(`datadog_login_tracked_${userId}`);
  }
};

export const trackUserSwitch = (previousUserId?: string, newUserId?: string, newRole?: string): void => {
  withDatadogRum(rum => {
    rum.addAction('user_switch', {
      previousUserId,
      newUserId,
      newRole,
    });

    rum.stopSession();

    if (newUserId) {
      const userInfo = buildDatadogUser({
        id: newUserId,
        role: newRole,
        ...getAppSourceContext(),
      });
      setDatadogUser(userInfo);
    }
  });

  if (typeof window !== 'undefined' && window.sessionStorage) {
    if (previousUserId) {
      sessionStorage.removeItem(`datadog_login_tracked_${previousUserId}`);
    }
    if (newUserId) {
      sessionStorage.setItem(`datadog_login_tracked_${newUserId}`, 'true');
    }
  }
};
